import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Row, Col, Form, Button, Table, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { savOperations } from "state/ducks/store/sav";

const euros = (cents) => `${((cents || 0) / 100).toFixed(2).replace(".", ",")} €`;

const dayString = (date) => date.toISOString().slice(0, 10);

const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return dayString(d);
};


/**
 * Le même sélecteur dans les deux modes : la liste de la période et l'écran
 * d'un seul SAV. Les familles ne sont pas sélectionnables ici — on reclasse
 * toujours vers une sous-catégorie précise.
 */
const CategorySelect = (props) => {
  const { grouped, value, disabled, onChange, size } = props;
  return (
    <Form.Control
      as="select"
      size={size}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">— Non catégorisé —</option>
      {grouped.map((family) => (
        <optgroup key={family.key} label={family.label}>
          {(family.subs || []).map((sub) => (
            <option key={sub.key} value={sub.key}>{sub.label}</option>
          ))}
        </optgroup>
      ))}
    </Form.Control>
  );
};

CategorySelect.propTypes = {
  grouped: PropTypes.array,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  size: PropTypes.string,
};

CategorySelect.defaultProps = { grouped: [] };

/**
 * Un seul SAV, ouvert par son id d'avantage — le lien « éditer la catégorie »
 * posé dans une colonne du dashboard de pilotage (demande de Tim du 30/10/2026).
 *
 * L'onglet ne peut pas se refermer seul : le navigateur refuse `window.close()`
 * sur un onglet ouvert par un clic sur un lien, et la page vit dans une iframe
 * d'une autre origine que celle du BO. On le dit donc clairement plutôt que de
 * promettre une fermeture qui n'arriverait pas.
 */
const UnSeulSav = (props) => {
  const { savId, grouped, onShowPeriod } = props;
  const dispatch = useDispatch();

  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [state, setState] = useState(null);

  useEffect(() => {
    let abandoned = false;
    setIsLoading(true);
    setError(null);
    setState(null);
    dispatch(savOperations.getSavAdvantage(savId)).then(
      (res) => {
        if (abandoned) return;
        const found = res?.data?.result?.item || null;
        setItem(found);
        setError(found ? null : `Le SAV #${savId} n'a pas été trouvé.`);
        setIsLoading(false);
      },
      (err) => {
        if (abandoned) return;
        setError(err?.response?.data?.message || `Le SAV #${savId} n'a pas pu être chargé.`);
        setIsLoading(false);
      }
    );
    return () => { abandoned = true; };
  }, [dispatch, savId]);

  const save = (savCategory) => {
    if (!savCategory || savCategory === item?.savCategory) {
      return;
    }
    setState("saving");
    dispatch(savOperations.updateSavAdvantageCategory(savId, savCategory)).then(
      (res) => {
        setItem(res?.data?.result?.item || { ...item, savCategory });
        setState("saved");
      },
      (err) => setState(err?.response?.data?.message || "Le reclassement n'a pas été enregistré")
    );
  };

  if (isLoading) {
    return (
      <Container>
        <p className="text-muted"><Spinner animation="border" size="sm" /> Chargement du SAV…</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={onShowPeriod}>Voir les SAV d'une période</Button>
      </Container>
    );
  }

  const name = [item.customer?.firstname, item.customer?.lastname].filter(Boolean).join(" ")
    || item.customer?.email
    || "—";

  return (
    <Container>
      <h5 className="mb-3">Catégorie de ce SAV</h5>

      {state === "saved" && (
        <Alert variant="success">
          <b><FontAwesomeIcon icon={faCheck} /> C'est enregistré.</b>
          <div>La correction est reprise dans les statistiques. Vous pouvez fermer cet onglet.</div>
        </Alert>
      )}

      <Row>
        <Col xs={12} lg={7}>
          <Table borderless size="sm" className="mb-3">
            <tbody>
              <tr><th className="text-muted font-weight-normal" style={{ width: 170 }}>Date</th>
                  <td>{(item.createdAt || "").slice(0, 10)}</td></tr>
              <tr><th className="text-muted font-weight-normal">Client</th>
                  <td>{name}</td></tr>
              <tr><th className="text-muted font-weight-normal">Commande</th>
                  <td>{item.order?.numberOrder || "—"}</td></tr>
              <tr><th className="text-muted font-weight-normal">Motif</th>
                  <td>{item.reason || "—"}</td></tr>
              <tr><th className="text-muted font-weight-normal">Concerné</th>
                  <td>{item.concernedProductsLabel || "—"}</td></tr>
              {item.cagnotteComputedAmount ? (
                <tr><th className="text-muted font-weight-normal">Montant crédité</th>
                    <td>{euros(item.cagnotteComputedAmount)}</td></tr>
              ) : null}
              {item.freshdeskTicketLink ? (
                <tr><th className="text-muted font-weight-normal">Ticket</th>
                    <td>
                      <a href={item.freshdeskTicketLink} target="_blank" rel="noopener noreferrer">
                        #{item.freshdeskTicketId} <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    </td></tr>
              ) : null}
            </tbody>
          </Table>

          <Form.Label>Catégorie</Form.Label>
          <CategorySelect
            grouped={grouped}
            value={item.savCategory}
            disabled={state === "saving"}
            onChange={save}
          />
          {state === "saving" && (
            <small className="text-muted"><Spinner animation="border" size="sm" /> Enregistrement…</small>
          )}
          {state && state !== "saving" && state !== "saved" && (
            <small className="text-danger">{state}</small>
          )}

          <Alert variant="light" className="border mt-3 mb-3">
            Seule la catégorie se modifie ici. Le montant crédité, la cagnotte et le
            message envoyé au client restent tels quels : un SAV déjà traité ne se
            réécrit pas.
          </Alert>

          <Button variant="outline-secondary" size="sm" onClick={onShowPeriod}>
            Voir les SAV d'une période
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

UnSeulSav.propTypes = {
  savId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  grouped: PropTypes.array,
  onShowPeriod: PropTypes.func,
};

UnSeulSav.defaultProps = { grouped: [] };

/**
 * Relecture des classements SAV : on sort une période, on corrige les catégories
 * mal choisies, et c'est tout. Ni le montant, ni la cagnotte, ni le message parti
 * au client ne se modifient ici — demande de Tim du 29/10/2026.
 *
 * La correction est reportée sur les avantages du SAV côté API, parce que ce sont
 * eux que lisent les statistiques : corriger la seule proposition ne changerait
 * rien aux chiffres du dashboard.
 */
const Reclasser = (props) => {
  const dispatch = useDispatch();

  // Arrivée depuis un lien du dashboard : on ouvre ce SAV-là, pas une période.
  // Le bouton « Voir les SAV d'une période » repasse ensuite en mode liste.
  const [singleSavId, setSingleSavId] = useState(props.savId || null);

  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(dayString(new Date()));
  const [category, setCategory] = useState("");
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Par id de SAV : "saving" pendant l'appel, "saved" une fois enregistré,
  // le message d'erreur sinon. L'agent doit voir ligne par ligne ce qui a pris.
  const [rowState, setRowState] = useState({});

  useEffect(() => {
    dispatch(savOperations.listPropositionCategories()).then(
      (res) => setGrouped(res?.data?.result?.savCategoriesGrouped || []),
      () => setGrouped([])
    );
  }, [dispatch]);

  const search = useCallback(() => {
    setIsLoading(true);
    setRowState({});
    dispatch(savOperations.searchPropositions({
      from,
      to,
      limit: 300,
      ...(category ? { savCategory: category } : {}),
    })).then(
      (res) => {
        setItems(res?.data?.result?.items || []);
        setIsLoading(false);
        setHasSearched(true);
      },
      () => {
        setItems([]);
        setIsLoading(false);
        setHasSearched(true);
      }
    );
  }, [dispatch, from, to, category]);

  useEffect(() => {
    if (singleSavId) {
      return;
    }
    search();
    // Au premier affichage seulement : ensuite c'est le bouton qui relance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSavId]);

  const reclass = (item, savCategory) => {
    if (!savCategory || savCategory === item.savCategory) {
      return;
    }
    setRowState((prev) => ({ ...prev, [item.id]: "saving" }));
    dispatch(savOperations.updatePropositionCategory(item.id, savCategory)).then(
      (res) => {
        const updated = res?.data?.result?.item;
        setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, ...(updated || { savCategory }) } : row)));
        setRowState((prev) => ({ ...prev, [item.id]: "saved" }));
      },
      (err) => {
        setRowState((prev) => ({
          ...prev,
          [item.id]: err?.response?.data?.message || "Le reclassement n'a pas été enregistré",
        }));
      }
    );
  };

  const families = grouped.map((family) => ({ key: family.key, label: family.label }));

  if (singleSavId) {
    return (
      <UnSeulSav
        savId={singleSavId}
        grouped={grouped}
        onShowPeriod={() => setSingleSavId(null)}
      />
    );
  }

  return (
    <Container>
      <Alert variant="light" className="border">
        Seule la catégorie se modifie ici. Le montant crédité, la cagnotte et le message
        envoyé au client restent tels quels : un SAV déjà traité ne se réécrit pas.
        La correction est reprise dans les statistiques.
      </Alert>

      <Row className="align-items-end mb-3">
        <Col xs={12} md={3} className="mb-2">
          <Form.Label>Du</Form.Label>
          <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Col>
        <Col xs={12} md={3} className="mb-2">
          <Form.Label>Au</Form.Label>
          <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Col>
        <Col xs={12} md={4} className="mb-2">
          <Form.Label>Famille ou catégorie</Form.Label>
          <Form.Control as="select" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Toutes</option>
            {families.map((family) => (
              <option key={family.key} value={family.key}>{family.label} (toute la famille)</option>
            ))}
            {grouped.map((family) => (
              <optgroup key={`g-${family.key}`} label={family.label}>
                {(family.subs || []).map((sub) => (
                  <option key={sub.key} value={sub.key}>{sub.label}</option>
                ))}
              </optgroup>
            ))}
          </Form.Control>
        </Col>
        <Col xs={12} md={2} className="mb-2">
          <Button variant="success" className="w-100" onClick={search} disabled={isLoading}>
            {isLoading ? "Recherche…" : "Afficher"}
          </Button>
        </Col>
      </Row>

      {!isLoading && hasSearched && items.length === 0 && (
        <Alert variant="warning">Aucun SAV sur cette période avec ce filtre.</Alert>
      )}

      {items.length > 0 && (
        <>
          <p className="text-muted small">
            {items.length} SAV — la liste s'arrête à 300, resserrez la période si elle est pleine.
          </p>
          <Table striped hover size="sm" responsive>
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Commande</th>
                <th style={{ minWidth: 260 }}>Catégorie</th>
                <th>Motif</th>
                <th>Concerné</th>
                <th className="text-right">Montant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const state = rowState[item.id];
                const name = [item.customer?.firstname, item.customer?.lastname].filter(Boolean).join(" ")
                  || item.customer?.email
                  || "—";
                return (
                  <tr key={item.id}>
                    <td className="text-nowrap">{(item.createdAt || "").slice(0, 10)}</td>
                    <td>{name}</td>
                    <td className="text-nowrap">{item.order?.numberOrder || "—"}</td>
                    <td>
                      <CategorySelect
                        grouped={grouped}
                        size="sm"
                        value={item.savCategory}
                        disabled={state === "saving"}
                        onChange={(savCategory) => reclass(item, savCategory)}
                      />
                      {state === "saving" && (
                        <small className="text-muted"><Spinner animation="border" size="sm" /> Enregistrement…</small>
                      )}
                      {state === "saved" && (
                        <small className="text-success"><FontAwesomeIcon icon={faCheck} /> Enregistré</small>
                      )}
                      {state && state !== "saving" && state !== "saved" && (
                        <small className="text-danger">{state}</small>
                      )}
                    </td>
                    <td className="small">{item.reason || "—"}</td>
                    <td className="small">{item.concernedProductsLabel || "—"}</td>
                    <td className="text-right text-nowrap">{euros(item.cagnotteComputedAmount)}</td>
                    <td className="text-nowrap">
                      {item.freshdeskTicketLink && (
                        <a href={item.freshdeskTicketLink} target="_blank" rel="noopener noreferrer" title="Ticket Freshdesk">
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 1rem 0;
`;

Reclasser.propTypes = {
  // Id d'avantage passé par l'URL (?savId=) : ouvre ce SAV seul.
  savId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Reclasser;
